import React from 'react'
import { useNavigate } from 'react-router-dom'
// view components
import { Box, Inline, Stack } from 'src/viewLayer/components/DesignSystem'
import useB2BSessionRequest from 'src/viewLayer/components/hooks/useB2BSessionRequest'
// context and data
import useTrackPageShown from 'src/viewLayer/components/hooks/useTrackPageShown'
import BackButton from 'src/viewLayer/components/pages/BackButton'
import IdentityProviderInput from 'src/viewLayer/components/shared/Inputs/IdentityProviderInput'
import { ResponsiveHeader } from 'src/viewLayer/components/shared/ResponsiveHeader'
import TrackableButton from 'src/viewLayer/components/shared/TrackableButton'
import { t } from 'src/viewLayer/helpers/translations'
import { useBusinessErrorPagePath } from 'src/viewLayer/navigation'

import useInputState from '../../hooks/useInputState'
import DataProcessingSection from './DataProcessingSection'
import { Card, CardLayout, FormContainer, LoginBusinessPageWithBackground } from './index.styled'
import validateAndFormatIdentityProvider from './validateAndFormatIdentityProvider'

export default function LoginBusinessPage({ appType, children }) {
  useTrackPageShown({ appType, pageType: 'b2b_sso_subdomain' })
  const navigate = useNavigate()
  const redirectPath = useBusinessErrorPagePath()

  const initIdentityProvider = {
    inputValue: '',
    isFocused: true,
  }
  const {
    errorKey: identityProviderErrorKey,
    inputValue: identityProvider,
    isFocused: identityProviderIsFocused,
    resetInputState: resetIdentityProviderState,
    setErrorKey: setIdentityProviderErrorKey,
    setInputValue: setIdentityProvider,
  } = useInputState(initIdentityProvider)

  const {
    identityProviderValidationRequestInProgress,
    isRedirecting,
    makeIdentityProviderValidationRequest,
  } = useB2BSessionRequest(identityProvider)

  const onSubmit = async (event) => {
    event.preventDefault()
    event.stopPropagation()
    try {
      await makeIdentityProviderValidationRequest()
    } catch (error) {
      // eslint-disable-next-line no-unused-expressions -- Legacy
      error === 'redirectToErrorPage'
        ? navigate(redirectPath, { replace: true })
        : setIdentityProviderErrorKey(error)
    }
  }

  const largeSizeHeader = 'large'

  return (
    <LoginBusinessPageWithBackground>
      <CardLayout align="center">
        <Card
          data-testid="login-business-page"
          padding={{
            small: [77, 0, 28, 0],
            xsmall: [40, 0, 37, 0],
            xxxsmall: [40, 16, 37, 16],
          }}
        >
          <BackButton path="business" trackingId="backbutton-business-to-email" />
          <Inline align="center" padding={{ small: [0, 0, 40], xxsmall: [26, 0, 40] }}>
            <ResponsiveHeader isCentered={false} largeSize={largeSizeHeader}>
              {t('email.title')}
            </ResponsiveHeader>
          </Inline>
          <Stack gap={{ small: 60, xxxsmall: 47 }}>
            <FormContainer>
              <form data-testid="login-business-page-form" onSubmit={onSubmit}>
                <IdentityProviderInput
                  clearValue={resetIdentityProviderState}
                  errorKey={identityProviderErrorKey}
                  isFocused={identityProviderIsFocused}
                  label={t('login.b2b.company_identity_provider')}
                  onChangeHandler={(event) =>
                    setIdentityProvider(validateAndFormatIdentityProvider(event))
                  }
                  value={identityProvider}
                />
              </form>
              <Box padding={[27, 0, 0, 0]}>
                <TrackableButton
                  dataTestid="login-business-page-submit-button"
                  disabled={
                    identityProvider.length === 0 ||
                    identityProviderValidationRequestInProgress ||
                    isRedirecting
                  }
                  fullWidth={true}
                  id="uat-continue-button"
                  inProgress={identityProviderValidationRequestInProgress || isRedirecting}
                  onClick={onSubmit}
                  title="Continue button"
                  trackingId="identity_provider_continue"
                  variant="primary"
                >
                  {t('email.button.continue')}
                </TrackableButton>
              </Box>
            </FormContainer>
            <DataProcessingSection />
          </Stack>
        </Card>
      </CardLayout>
      {children}
    </LoginBusinessPageWithBackground>
  )
}
